import * as React from 'react';

import {
  TRecord,
  TTypeReference,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {
  TypeImplementation,
  Plugins,
  withSystem,
  setType,
  getSystem,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
  getAnn,
} from '@packages/utilities/contextual-expression';
import {weakMemo} from '@packages/utilities/memo';
import {qualify, pluginName, typeName} from '@packages/systems/wfdl-Qualified';
import {TEffect, THttpResponse} from '@packages/systems/plugins/Effect';
import {tyfun} from '@packages/utilities/wfdl-helpers';
import {
  ExpressionEditor,
  extendExpressionEditorActionAtPath,
  forwardDispatch,
} from '@packages/systems/ExpressionEditor-utils';

import {styled, styledDiv} from '@packages/utilities/styled';
import {Subheading, FinePrint} from '@packages/systems/design-system/Text';

import {LogicLabel} from '@packages/systems/flows/shared';

const {getRecord} = ContextualExpressions;

const RequestType = TRecord({
  URL: withSystem(
    {bindUIVariant: 'custom', label: 'URL', labelVariant: 'hidden'},
    TTypeConstructor(qualify(pluginName('Http'), typeName('Request URL')))
  ),
  Authentication: withSystem(
    {bindUIVariant: 'custom'},
    TTypeConstructor(
      qualify(pluginName('Http'), typeName('Request Authentication'))
    )
  ),
  Headers: withSystem(
    {bindUIVariant: 'custom'},
    TTypeConstructor(qualify(Plugins.name('Http'), typeName('Request Headers')))
  ),
  Method: withSystem(
    {bindUIVariant: 'custom'},
    TTypeConstructor(qualify(Plugins.name('Http'), typeName('Request Method')))
  ),
  'Response Parser': withSystem(
    {isFullWidth: true},
    tyfun(
      [THttpResponse],
      TEffect(TTypeReference('error'), TTypeReference('output'))
    )
  ),
});

const urlPath = {in: 'Record', at: 'URL'} as const;
const authenticationPath = {in: 'Record', at: 'Authentication'} as const;
const headersPath = {in: 'Record', at: 'Headers'} as const;
const methodPath = {in: 'Record', at: 'Method'} as const;

const getPaths = (annExpr: ContextualExpression) => {
  const rec = getRecord(stripAnn(annExpr));

  const urlExpr = rec?.URL;
  const authenticationExpr = rec?.Authentication;
  const headersExpr = rec?.Headers;
  const methodExpr = rec?.Method;

  // The default expression for the body value is annotated as private
  // such that we don't render the input field for the Request Method
  const privateBodyExpr = methodExpr
    ? ContextualExpressions.getUnion(stripAnn(methodExpr))?.value
    : null;

  // We'll update it to be non-private so as to be able to render the input field
  const bodyType = privateBodyExpr ? getAnn(privateBodyExpr).type : null;
  const bodyExpr =
    privateBodyExpr && bodyType
      ? setType(
          privateBodyExpr,
          withSystem({...getSystem(bodyType), private: false}, bodyType)
        )
      : null;

  if (
    urlExpr == null ||
    authenticationExpr == null ||
    headersExpr == null ||
    methodExpr == null ||
    bodyExpr == null
  ) {
    return null;
  }

  return {
    urlExpr,
    authenticationExpr,
    headersExpr,
    methodExpr,
    bodyExpr,
  };
};

const GridWrapper = styledDiv(({theme: {gutters}}) => ({
  display: 'grid',
  gridAutoColumns: 'minmax(0, auto)',
  gridRowGap: gutters.x8,
}));

const Divider = styledDiv(({theme: {gutters, colors}}) => ({
  height: 1,
  backgroundColor: colors.border,
  width: `calc(100% + ${gutters.x8 * 2 + gutters.x4}px)`,
  marginLeft: -gutters.x8 - gutters.x4 / 2,
  gridColumn: '1 / -1',
}));

const StyledSubheading = styled(Subheading)(({theme: {gutters}}) => ({
  marginBottom: gutters.x4,
}));

const StyledFinePrint = styled(FinePrint)(({theme: {colors, gutters}}) => ({
  color: colors.logicFlows.foregroundDimAlt,
  marginBottom: gutters.x4 / 2,
}));

const getMethodValue = weakMemo((annExpr: ContextualExpression) => {
  const strippedExpr = stripAnn(annExpr);
  return ContextualExpressions.getUnion(strippedExpr)?.tag || '';
});

export const RequestGenericType = TypeImplementation.Alias({
  type: RequestType,
  params: ['error', 'output'],
  render: function RenderRequestTypeGeneric({expr, dispatch}) {
    const paths = React.useMemo(() => getPaths(expr), [expr]);

    if (paths == null) {
      return <ExpressionEditor dispatch={dispatch} expression={expr} />;
    }

    const methodValue = getMethodValue(paths.methodExpr);

    const onBodyChange = weakMemo(
      extendExpressionEditorActionAtPath(dispatch)([
        methodPath,
        {tag: methodValue, in: 'Union'},
      ])
    );

    return (
      <GridWrapper>
        <div>
          <div>
            <StyledSubheading>Authentication</StyledSubheading>
            <div style={{marginRight: 0}}>
              <ExpressionEditor
                dispatch={forwardDispatch(dispatch)(authenticationPath)}
                expression={paths.authenticationExpr}
              />
            </div>
          </div>
          <Divider />
        </div>

        <div>
          <ExpressionEditor
            dispatch={forwardDispatch(dispatch)(methodPath)}
            expression={paths.methodExpr}
          />
        </div>

        <Divider />
        <div>
          <StyledSubheading>General</StyledSubheading>

          <div>
            <LogicLabel label="URL" required>
              <StyledFinePrint>Add the URL for the service.</StyledFinePrint>
              <ExpressionEditor
                dispatch={forwardDispatch(dispatch)(urlPath)}
                expression={paths.urlExpr}
              />
            </LogicLabel>
          </div>
        </div>

        <div>
          <LogicLabel label="Headers">
            <ExpressionEditor
              dispatch={forwardDispatch(dispatch)(headersPath)}
              expression={paths.headersExpr}
            />
          </LogicLabel>
        </div>

        {/** We'll only show the "Body" input for methods that can utilize it */}
        {(methodValue === 'POST' ||
          methodValue === 'PUT' ||
          methodValue === 'PATCH') && (
          <div>
            <ExpressionEditor
              dispatch={onBodyChange}
              expression={paths.bodyExpr}
            />
          </div>
        )}
      </GridWrapper>
    );
  },
});
